// Chain verifier — the no-LLM fallback for "narrated working" transcripts, e.g.
//   "3 times 3 times 3 is 27, take away 3 is 24"
// The fuzzy matcher (spokenMatch) misses these because the intermediate results (27)
// don't appear in the final-expression signature. Instead of matching a template, this
// VERIFIES the stated working: it reads "<expr> is <result>" assertions, checks each
// one's arithmetic with the exact-rational evaluator, carries forward elided operands
// ("take away 3" → previousResult − 3), and confirms the chain consumes all four
// original numbers and lands on 24. A verified chain is high-confidence (arithmetic, not
// fuzz), so it can auto-fire. Messy transcripts simply don't verify → typed fallback.

import { evaluateExpression } from './evaluate'

type Tok = { kind: 'num'; value: number } | { kind: 'op'; value: '+' | '-' | '*' | '/' } | { kind: 'eq' }

const UNITS: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  to: 2, too: 2, for: 4, fore: 4, ate: 8, won: 1, tree: 3, free: 3,
}
const TEENS: Record<string, number> = {
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19,
}
const TENS: Record<string, number> = {
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
}
const OP1: Record<string, '+' | '-' | '*' | '/'> = {
  times: '*', x: '*', multiplied: '*', multiply: '*', product: '*',
  plus: '+', add: '+', added: '+',
  minus: '-', subtract: '-', subtracted: '-', less: '-', takeaway: '-',
  divided: '/', divide: '/', divides: '/', over: '/',
}
const EQ = new Set(['is', 'equals', 'equal', 'makes', 'make', 'gives', 'give', 'leaves', 'leave', 'becomes'])

const parseNumberAt = (w: string[], i: number): { value: number; len: number } | null => {
  const x = w[i]
  if (/^\d+$/.test(x)) return { value: parseInt(x, 10), len: 1 }
  if (TENS[x] !== undefined) {
    const n = w[i + 1]
    if (n && UNITS[n] !== undefined && UNITS[n] >= 1 && UNITS[n] <= 9) return { value: TENS[x] + UNITS[n], len: 2 }
    return { value: TENS[x], len: 1 }
  }
  if (TEENS[x] !== undefined) return { value: TEENS[x], len: 1 }
  if (UNITS[x] !== undefined) return { value: UNITS[x], len: 1 }
  return null
}

const tokenize = (text: string): Tok[] => {
  const s = (text || '')
    .toLowerCase()
    .replace(/[×]/g, ' * ')
    .replace(/[÷]/g, ' / ')
    .replace(/([a-z])-([a-z])/g, '$1 $2') // twenty-seven → twenty seven
  const w = s.split(/[^a-z0-9+\-*/=]+/).filter(Boolean)
  const out: Tok[] = []
  let i = 0
  while (i < w.length) {
    const num = parseNumberAt(w, i)
    if (num) {
      out.push({ kind: 'num', value: num.value })
      i += num.len
      continue
    }
    if (w[i] === 'take' && w[i + 1] === 'away') {
      out.push({ kind: 'op', value: '-' })
      i += 2
      continue
    }
    const cur = w[i]
    if (OP1[cur]) {
      out.push({ kind: 'op', value: OP1[cur] })
      i++
      continue
    }
    if (cur === '+' || cur === '-' || cur === '*' || cur === '/') {
      out.push({ kind: 'op', value: cur })
      i++
      continue
    }
    if (cur === '=' || EQ.has(cur)) {
      out.push({ kind: 'eq' })
      i++
      continue
    }
    i++ // filler — skip
  }
  return out
}

// Split the token stream into (segment, asserted-result) pairs around each "= <number>".
const toAssertions = (toks: Tok[]): { seg: Tok[]; result: number }[] => {
  const pairs: { seg: Tok[]; result: number }[] = []
  let seg: Tok[] = []
  let i = 0
  while (i < toks.length) {
    const t = toks[i]
    if (t.kind === 'eq') {
      const next = toks[i + 1]
      if (next && next.kind === 'num') {
        pairs.push({ seg, result: next.value })
        seg = []
        i += 2
        continue
      }
      i++ // dangling "=" with no number — ignore
      continue
    }
    seg.push(t)
    i++
  }
  return pairs
}

const tokensToStr = (toks: Tok[]): string =>
  toks.map((t) => (t.kind === 'num' ? String(t.value) : t.kind === 'op' ? t.value : '')).join('')

const removeOne = (arr: number[], v: number): boolean => {
  const idx = arr.indexOf(v)
  if (idx === -1) return false
  arr.splice(idx, 1)
  return true
}

export type ChainResult = { solved: boolean; expression: string | null }

export const verifyChain = (text: string, numbers: number[]): ChainResult => {
  const assertions = toAssertions(tokenize(text))
  if (assertions.length === 0) return { solved: false, expression: null }

  let value: number | null = null // running result
  let available = [...numbers] // original numbers not yet consumed
  let exprStr: string | null = null // human-readable reconstruction

  for (const { seg, result } of assertions) {
    let s = seg
    // Drop a restated previous result at the front ("…is 27, 27 minus 3…").
    if (value !== null && s.length && s[0].kind === 'num' && s[0].value === value) s = s.slice(1)

    const carry = value !== null && s.length > 0 && s[0].kind === 'op'
    if (!carry) {
      // Fresh expression / restart — reset the chain to a clean slate.
      value = null
      available = [...numbers]
      exprStr = null
    }

    const usedNums = s.filter((t) => t.kind === 'num').map((t) => (t as any).value as number)

    // The original numbers this step uses must all still be available.
    const trial = [...available]
    if (!usedNums.every((n) => removeOne(trial, n))) continue // can't be a real step here

    const expr = carry ? `(${value})${tokensToStr(s)}` : tokensToStr(s)
    const evald = evaluateExpression(expr)
    if (!evald.ok || evald.value === null) continue
    if (Math.abs(evald.value - result) > 1e-9) continue // stated result is wrong → skip (exploration)

    // Accept the step.
    available = trial
    value = result
    exprStr = carry && exprStr ? `(${exprStr})${tokensToStr(s)}` : tokensToStr(s)
  }

  const solved = value !== null && Math.abs(value - 24) < 1e-9 && available.length === 0
  return { solved, expression: solved ? exprStr : null }
}
